import * as React from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { toastError } from '@/components/ui/toast';
import { useQueryParamState } from '@/hooks/use-query-params';

import type {
  OpsActionResult,
  OpsDetail,
  OpsIndexEntry,
  OpsKey,
  OpsListResponse,
} from './types';

/** Which URL params each page passes through to the API. */
const FILTER_PARAMS: Record<OpsKey, string[]> = {
  'policy-violations': [
    'q',
    'status',
    'severity',
    'hard',
    'agent',
    'date_from',
    'date_to',
    'range', 'from', 'to', 'day', 'tz',
    'sort',
    'page',
  ],
  'performance-inbox': ['q', 'status', 'agent', 'severity', 'category', 'page'],
  'manual-review': ['review', 'page'],
  'routing-audit': ['status', 'page'],
  voicemails: ['q', 'tab', 'mailbox', 'status', 'type', 'priority', 'read', 'sort', 'page'],
  departments: ['q', 'active', 'phone', 'voicemail', 'sort', 'page'],
  contacts: ['q', 'source', 'fraud', 'sort', 'page'],
  'sip-phones': ['q', 'claim', 'registered', 'active', 'env', 'locked', 'sort', 'page'],
  'user-sessions': [
    'q',
    'user',
    'app_version',
    'session_status',
    'device',
    'twilio',
    'call_state',
    'date_from',
    'date_to',
    'sort',
    'per_page',
    'page',
  ],
};

export function opsFilterParams(key: OpsKey): string[] {
  return FILTER_PARAMS[key] ?? ['status', 'page'];
}

/**
 * Console route for a page. Review queues sit under /queues and operational
 * records under /ops, matching the sidebar sections and the route map the
 * backend hands out in the bootstrap payload.
 */
const ROUTES: Record<OpsKey, string> = {
  'policy-violations': '/queues/policy-violations',
  'performance-inbox': '/queues/performance-inbox',
  'manual-review': '/queues/manual-review',
  'routing-audit': '/queues/routing-audit',
  voicemails: '/ops/voicemails',
  departments: '/ops/departments',
  contacts: '/ops/contacts',
  'sip-phones': '/ops/sip-phones',
  'user-sessions': '/ops/sessions',
};

export function opsRoute(key: OpsKey, pk?: number | string): string {
  const base = ROUTES[key];
  return pk === undefined ? base : `${base}/${pk}`;
}

export function useOpsIndex() {
  return useQuery({
    queryKey: queryKeys.ops.index,
    queryFn: () => api.get<{ pages: OpsIndexEntry[] }>('/ops/'),
    // Sidebar badges; avoid hammering the index while navigating the shell.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * A page's current result set, driven entirely by the URL.
 *
 * Only the params the page actually understands are forwarded, so an unrelated
 * console param (a leftover tab, say) cannot change the result set or split the
 * cache key.
 */
export function useOpsList(key: OpsKey) {
  const { asObject } = useQueryParamState();
  const allowed = opsFilterParams(key);

  const params = React.useMemo(() => {
    const next: Record<string, string> = {};
    for (const name of allowed) {
      const value = asObject[name];
      if (value) next[name] = value;
    }
    return next;
  }, [asObject, allowed]);

  const query = useQuery({
    queryKey: queryKeys.ops.list(key, params),
    queryFn: () => api.get<OpsListResponse>(`/ops/${key}/`, params),
    // Hold the previous result while a search or filter refetches. The shell
    // renders its filter bar from `data`, so dropping to undefined unmounted
    // the search box mid-keystroke and took focus with it.
    placeholderData: keepPreviousData,
    // These are triage surfaces: a stale count is worse than an extra request.
    staleTime: 5_000,
  });

  return { ...query, params };
}

export function useOpsDetail(key: OpsKey, pk: string | number | undefined) {
  return useQuery({
    queryKey: queryKeys.ops.detail(key, pk ?? ''),
    queryFn: () => api.get<{ detail: OpsDetail }>(`/ops/${key}/${pk}/`),
    enabled: pk !== undefined && pk !== '',
    select: (data) => data.detail,
  });
}

/**
 * Run a workflow transition.
 *
 * Every ops view in the cache is invalidated on success, because a transition
 * changes the counts on the status tabs as well as the row itself.
 */
export function useOpsAction(key: OpsKey) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { action: string; pks: Array<number | string>; note?: string }) =>
      api.post<OpsActionResult>(`/ops/${key}/action/`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.ops.all });
    },
    onError: (error) => toastError(error),
  });
}

/**
 * Actions offered for a row in a given state.
 *
 * `hide_for_status` comes from the server so the console and the Django admin
 * agree on which transitions are legal from where.
 */
export function visibleActions<T extends { hide_for_status: string[]; detail_only: boolean }>(
  actions: T[],
  status: string,
  { includeDetailOnly = false }: { includeDetailOnly?: boolean } = {},
): T[] {
  return actions.filter((action) => {
    if (action.detail_only && !includeDetailOnly) return false;
    return !action.hide_for_status.includes(status);
  });
}
